import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id", "text", "position", "children", "visible", "defaultVisible"];
import React, { cloneElement, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { positions, styles } from './constant';
import ScopedCssBaseline from '../StyleBase/ScopedCssBaseline';
import { targetWithin } from '../helper';
import { useControlled, useEventListener, useMergeRefs, useId } from '../hooks';
const DISPLAY_NAME = 'Tooltip';
const Root = styled.div.withConfig({
  displayName: "Tooltip__Root",
  componentId: "sc-ou6t7i-0"
})(["display:", ";position:absolute;z-index:var(--zindex-2);border-radius:var(--border-radius-rounded);padding:var(--size-spacing-02) var(--size-spacing-03);color:var(--neutral-text-inverse);font:var(--text-sm-regular);line-height:20px;text-align:center;letter-spacing:-0.14px;background:var(--neutral-surface-inverse);:after{content:'';position:absolute;width:0;height:0;border-style:solid;}", " ", " > span{display:block;box-sizing:border-box;word-break:break-word;width:max-content;max-width:calc(250px - (var(--size-spacing-03) * 2));}"], _ref => {
  let {
    $visible
  } = _ref;
  return $visible ? 'block' : 'none';
}, _ref2 => {
  let {
    $position
  } = _ref2;
  const {
    textAlign,
    transform,
    borderWidth,
    borderColor
  } = styles[$position];
  return css(["", ":calc((var(--size-spacing-03) + 6px) * -1);text-align:", ";transform:", ";:after{border-width:", ";border-color:", ";transform:", ";}"], $position, textAlign, transform, borderWidth, borderColor, transform);
}, _ref3 => {
  let {
    $position
  } = _ref3;
  if ($position === positions.bottom) return css(["left:50%;:after{top:calc(var(--size-spacing-03) * -1);left:50%;}"]);
  if ($position === positions.right) return css(["top:50%;:after{left:calc(var(--size-spacing-03) * -1);top:50%;}"]);
  if ($position === positions.left) return css(["top:50%;:after{right:calc(var(--size-spacing-03) * -1);top:50%;}"]);

  // default is Top
  return css(["left:50%;:after{bottom:calc(var(--size-spacing-03) * -1);left:50%;}"]);
});
const Wrapper = styled.div.withConfig({
  displayName: "Tooltip__Wrapper",
  componentId: "sc-ou6t7i-1"
})(["", " position:relative;"], ScopedCssBaseline);
const Tooltip = /*#__PURE__*/forwardRef((_ref4, ref) => {
  let {
      id: idProp,
      text,
      position,
      children,
      visible: propVisible,
      defaultVisible: propDefaultVisible
    } = _ref4,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded);
  const tooltipRef = useRef();
  const id = useId(idProp);
  const [visible, setVisible] = useControlled({
    value: propVisible,
    defaultValue: Boolean(propDefaultVisible)
  });
  const contains = event => targetWithin(event.target, [tooltipRef.current]);
  const switchVisibility = (state, event) => {
    if (!contains(event)) return;

    // remove title to prevent native tooltip to show
    if (state) {
      event.target.removeAttribute('title');
      const nodeList = event.target.querySelectorAll(':not(svg) > title');
      if (nodeList.length) [...nodeList].map(e => e.remove());
    }
    setVisible(state);
  };

  // event listeners for tooltip wrapper
  useEventListener('mouseover', event => switchVisibility(true, event));
  useEventListener('mouseout', event => switchVisibility(false, event));
  const getChildWithOverwrittenEvents = () => {
    if (typeof children === 'string') return children;
    const triggerProps = {
      onMouseOver: event => switchVisibility(true, event),
      onMouseOut: event => switchVisibility(false, event),
      onFocus: event => switchVisibility(true, event),
      onBlur: event => switchVisibility(false, event)
    };
    const accessibleProps = visible ? {
      'aria-describedby': id
    } : {};
    return /*#__PURE__*/cloneElement(children, Object.assign({}, triggerProps, accessibleProps, children.props));
  };
  return /*#__PURE__*/React.createElement(Wrapper, _extends({}, rest, {
    ref: useMergeRefs([tooltipRef, ref])
  }), getChildWithOverwrittenEvents(), /*#__PURE__*/React.createElement(Root, {
    id: id,
    role: "tooltip",
    $position: position,
    $visible: visible
  }, /*#__PURE__*/React.createElement("span", null, text)));
});
Tooltip.displayName = DISPLAY_NAME;
Tooltip.Style = Root;
Tooltip.WrapperStyle = Wrapper;
Tooltip.positions = positions;
Tooltip.defaultProps = {
  position: Tooltip.positions.top
};
process.env.NODE_ENV !== "production" ? Tooltip.propTypes = {
  /**
   * Sets the id. Responsible to connect accessibly tip id in other element. The id value is used in `aria-describedby`.
   */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Sets the `Tooltip` text.
   */
  text: PropTypes.string.isRequired,
  /**
   * Sets the position. Supporting attributes `Tooltip.positions.TOP`, `Tooltip.positions.RIGHT`,
   * `Tooltip.positions.BOTTOM` and `Tooltip.positions.LEFT` are deprecated.
   */
  position: PropTypes.oneOf([Tooltip.positions.TOP, Tooltip.positions.RIGHT, Tooltip.positions.BOTTOM, Tooltip.positions.LEFT, Tooltip.positions.top, Tooltip.positions.right, Tooltip.positions.bottom, Tooltip.positions.left])
} : void 0;
export default Tooltip;